import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import AuthContext from '../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const statusConfig = {
  pending:   { label: 'Pendente',   color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  sent:      { label: 'Enviada',    color: 'bg-blue-100 text-blue-800 border-blue-200' },
  overdue:   { label: 'Vencida',    color: 'bg-red-100 text-red-800 border-red-200' },
  paid:      { label: 'Paga',       color: 'bg-green-100 text-green-800 border-green-200' },
  cancelled: { label: 'Cancelada',  color: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const contractStatusConfig = {
  active:    { label: 'Ativo',      color: 'bg-green-100 text-green-800' },
  pending:   { label: 'Pendente',   color: 'bg-yellow-100 text-yellow-800' },
  suspended: { label: 'Suspenso',   color: 'bg-orange-100 text-orange-800' },
  cancelled: { label: 'Cancelado',  color: 'bg-red-100 text-red-800' },
  finished:  { label: 'Finalizado', color: 'bg-gray-100 text-gray-600' },
};

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('pt-BR');
}

function daysRemaining(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
}

function isOverdue(dueDateStr, status) {
  if (!dueDateStr || status === 'paid' || status === 'cancelled') return false;
  const due = new Date(dueDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

export default function ClientPortalPage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('financeiro');

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState('all');

  // Contracts state
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  const clientId = user?.clientId || user?.client_id || user?.id;

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` },
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (activeTab === 'financeiro') loadInvoices();
    if (activeTab === 'frota') loadContracts();
  }, [activeTab, user]);

  const loadInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const params = clientId ? `?clientId=${clientId}` : '';
      const res = await axios.get(`${API_URL}/api/invoices${params}`, axiosConfig);
      const data = res.data?.invoices || res.data?.data || res.data || [];
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Erro ao carregar faturas');
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const loadContracts = async () => {
    setContractsLoading(true);
    try {
      const params = new URLSearchParams({ status: 'active' });
      if (clientId) params.append('clientId', clientId);
      const res = await axios.get(`${API_URL}/api/contracts?${params}`, axiosConfig);
      const data = res.data?.contracts || res.data?.data || res.data || [];
      setContracts(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Erro ao carregar contratos');
      setContracts([]);
    } finally {
      setContractsLoading(false);
    }
  };

  const handleCopyPix = (pixCode) => {
    if (!pixCode) { toast.error('Código PIX não disponível'); return; }
    navigator.clipboard.writeText(pixCode)
      .then(() => toast.success('Código PIX copiado!'))
      .catch(() => {
        // fallback for older browsers
        const el = document.createElement('textarea');
        el.value = pixCode;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        toast.success('Código PIX copiado!');
      });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(inv => {
    if (invoiceFilter === 'all') return true;
    if (invoiceFilter === 'open') return inv.status === 'pending' || inv.status === 'sent';
    if (invoiceFilter === 'overdue') return inv.status === 'overdue' || isOverdue(inv.due_date, inv.status);
    if (invoiceFilter === 'paid') return inv.status === 'paid';
    return true;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f0f4f8' }}>
      {/* Header */}
      <header style={{ backgroundColor: '#2B3E5C' }} className="shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow">
              <span style={{ color: '#2B3E5C' }} className="font-black text-lg">V</span>
            </div>
            <div>
              <h1 className="text-white font-bold text-xl leading-tight">VIPLOC</h1>
              <p className="text-blue-200 text-xs">Portal do Cliente</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-blue-100 text-sm hidden sm:block">
              Olá, <span className="font-semibold text-white">{user?.name || user?.email}</span>
            </span>
            <button
              onClick={handleLogout}
              className="text-sm px-4 py-2 rounded-lg border border-blue-300 text-blue-100 hover:bg-blue-700 hover:text-white transition-colors"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      {/* Tab Bar */}
      <div style={{ backgroundColor: '#2B3E5C' }} className="border-t border-blue-700">
        <div className="max-w-6xl mx-auto px-4 flex gap-1">
          <button
            onClick={() => setActiveTab('financeiro')}
            className={`px-6 py-3 text-sm font-medium transition-colors rounded-t-lg -mb-px ${
              activeTab === 'financeiro'
                ? 'bg-white text-gray-800 border-t-2 border-blue-500'
                : 'text-blue-200 hover:text-white'
            }`}
          >
            Financeiro
          </button>
          <button
            onClick={() => setActiveTab('frota')}
            className={`px-6 py-3 text-sm font-medium transition-colors rounded-t-lg -mb-px ${
              activeTab === 'frota'
                ? 'bg-white text-gray-800 border-t-2 border-blue-500'
                : 'text-blue-200 hover:text-white'
            }`}
          >
            Minha Frota
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">

        {/* ===== TAB: FINANCEIRO ===== */}
        {activeTab === 'financeiro' && (
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold text-gray-800 mr-2">Faturas</h2>
              {['all', 'open', 'overdue', 'paid'].map(f => (
                <button
                  key={f}
                  onClick={() => setInvoiceFilter(f)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    invoiceFilter === f
                      ? 'border-blue-600 text-white'
                      : 'border-gray-300 text-gray-600 bg-white hover:border-blue-400'
                  }`}
                  style={invoiceFilter === f ? { backgroundColor: '#2B3E5C', borderColor: '#2B3E5C' } : {}}
                >
                  {{ all: 'Todos', open: 'Em Aberto', overdue: 'Vencidos', paid: 'Pagos' }[f]}
                </button>
              ))}
            </div>

            {invoicesLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#2B3E5C' }}></div>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <div className="text-5xl mb-3">📄</div>
                <p className="font-medium">Nenhuma fatura encontrada</p>
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ backgroundColor: '#2B3E5C' }} className="text-white">
                        <th className="px-4 py-3 text-left font-semibold">Fatura</th>
                        <th className="px-4 py-3 text-left font-semibold">Competência</th>
                        <th className="px-4 py-3 text-left font-semibold">Vencimento</th>
                        <th className="px-4 py-3 text-right font-semibold">Valor</th>
                        <th className="px-4 py-3 text-center font-semibold">Status</th>
                        <th className="px-4 py-3 text-center font-semibold">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredInvoices.map(inv => {
                        const overdue = isOverdue(inv.due_date, inv.status);
                        const st = statusConfig[inv.status] || { label: inv.status, color: 'bg-gray-100 text-gray-600 border-gray-200' };
                        const periodLabel = inv.reference_month
                          ? new Date(inv.reference_month + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
                          : (inv.period || '-');
                        return (
                          <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 font-mono text-gray-700">{inv.invoice_number || inv.id}</td>
                            <td className="px-4 py-3 text-gray-600 capitalize">{periodLabel}</td>
                            <td className={`px-4 py-3 font-medium ${overdue ? 'text-red-600' : 'text-gray-700'}`}>
                              {formatDate(inv.due_date)}
                              {overdue && <span className="ml-1 text-xs text-red-500">(Vencida)</span>}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-800">
                              {formatCurrency(inv.total_amount || inv.amount)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${st.color}`}>
                                {st.label}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                {inv.pdf_url && (
                                  <button
                                    onClick={() => window.open(inv.pdf_url)}
                                    className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 transition-colors"
                                  >
                                    PDF
                                  </button>
                                )}
                                {inv.boleto_url && (
                                  <button
                                    onClick={() => window.open(inv.boleto_url)}
                                    className="px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
                                  >
                                    2ª Via Boleto
                                  </button>
                                )}
                                {inv.pix_copy_paste && (
                                  <button
                                    onClick={() => handleCopyPix(inv.pix_copy_paste)}
                                    className="px-3 py-1.5 text-xs rounded-lg bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition-colors"
                                  >
                                    PIX
                                  </button>
                                )}
                                {!inv.pdf_url && !inv.boleto_url && !inv.pix_copy_paste && (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {filteredInvoices.map(inv => {
                    const overdue = isOverdue(inv.due_date, inv.status);
                    const st = statusConfig[inv.status] || { label: inv.status, color: 'bg-gray-100 text-gray-600 border-gray-200' };
                    const periodLabel = inv.reference_month
                      ? new Date(inv.reference_month + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
                      : (inv.period || '-');
                    return (
                      <div key={inv.id} className="bg-white rounded-xl shadow p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-sm text-gray-600">{inv.invoice_number || inv.id}</span>
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${st.color}`}>
                            {st.label}
                          </span>
                        </div>
                        <div className="text-gray-500 text-xs capitalize mb-1">{periodLabel}</div>
                        <div className={`text-sm font-medium mb-2 ${overdue ? 'text-red-600' : 'text-gray-700'}`}>
                          Venc: {formatDate(inv.due_date)}{overdue && ' (Vencida)'}
                        </div>
                        <div className="text-xl font-bold text-gray-800 mb-3">
                          {formatCurrency(inv.total_amount || inv.amount)}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {inv.pdf_url && (
                            <button onClick={() => window.open(inv.pdf_url)}
                              className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 border border-gray-200">
                              PDF Fatura
                            </button>
                          )}
                          {inv.boleto_url && (
                            <button onClick={() => window.open(inv.boleto_url)}
                              className="px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
                              2ª Via Boleto
                            </button>
                          )}
                          {inv.pix_copy_paste && (
                            <button onClick={() => handleCopyPix(inv.pix_copy_paste)}
                              className="px-3 py-1.5 text-xs rounded-lg bg-green-50 text-green-700 border border-green-200">
                              Copiar PIX
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ===== TAB: MINHA FROTA ===== */}
        {activeTab === 'frota' && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-4">Minha Frota</h2>

            {contractsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#2B3E5C' }}></div>
              </div>
            ) : contracts.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <div className="text-5xl mb-3">🚛</div>
                <p className="font-medium">Nenhum contrato ativo encontrado</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contracts.map(c => {
                  const days = daysRemaining(c.end_date);
                  const st = contractStatusConfig[c.status] || { label: c.status, color: 'bg-gray-100 text-gray-600' };
                  const daysColor = days === null ? '' : days < 30 ? 'bg-red-100 text-red-700' : days < 90 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700';
                  return (
                    <div key={c.id} className="bg-white rounded-xl shadow p-5 flex flex-col gap-3">
                      {/* Placa */}
                      <div className="flex items-center justify-between">
                        <div className="px-4 py-2 rounded-lg border-2 border-gray-300 text-center">
                          <span className="font-black text-2xl tracking-widest text-gray-800 uppercase">
                            {c.license_plate || c.vehicle_plate || '—'}
                          </span>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.color}`}>
                          {st.label}
                        </span>
                      </div>

                      {/* Veículo */}
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">
                          {[c.vehicle_brand, c.vehicle_model].filter(Boolean).join(' ') || c.vehicle_name || 'Veículo'}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">Contrato {c.contract_number || c.id}</p>
                      </div>

                      {/* Datas */}
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                        <div>
                          <span className="block text-gray-400">Início</span>
                          <span className="font-medium text-gray-700">{formatDate(c.start_date)}</span>
                        </div>
                        <div>
                          <span className="block text-gray-400">Fim</span>
                          <span className="font-medium text-gray-700">{formatDate(c.end_date)}</span>
                        </div>
                      </div>

                      {/* Mensalidade */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Mensalidade</span>
                        <span className="font-bold text-gray-800">{formatCurrency(c.monthly_rate)}</span>
                      </div>

                      {/* Dias restantes */}
                      {days !== null && (
                        <div className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-center ${daysColor}`}>
                          {days > 0 ? `${days} dias restantes` : days === 0 ? 'Vence hoje' : `Vencido há ${Math.abs(days)} dias`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
